import {
	AdminProductInfoState,
	DdiziQueryState,
	DdiziState,
	ExitQueryState,
	OrderState,
	PosElectronStoreState,
	PrintReceiptState,
	StoreInfoState,
} from '@kinderlabs-pos/state';
import { SelectableListItem, SimpleScrollbar, WithDialog } from '@kinderlabs-pos/ui-atoms';
import { Checkbox, List, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import { beaconTagCountAtom } from './ExitPaymentCard';
import { DetailButton } from '../../components/PosHeader/buttons/DetailButton';
import { TicketCompleteButton } from '../../modules/tickets/components/buttons/TicketCompleteButton';
import { TicketPrintButton } from '../../modules/tickets/components/buttons/TicketPrintButton';
import { TickeTimeMinusButton } from '../../modules/tickets/components/buttons/TicketTimeMinusButton';
import { TickeTimePlusButton } from '../../modules/tickets/components/buttons/TicketTimePlusButton';
import { DdiziDetailDialog } from '../DdiziManagementPage/DdiziDetailDialog';
import { DdiziEnterExitButtons } from '../DdiziManagementPage/DdiziEnterExitButtons';
import { ExitPageDdiziDisplay } from './ExitPageDdiziDisplay';
import { barcodeBackendAxios } from '../../modules/mappingTicket/barcodeBackendAxios';
import { MappingTicketApis } from '../../modules/mappingTicket/mappingTicketApis';
import { TicketMappingInfoType } from '../../modules/mappingTicket/mappingTicketType';

export const ExitPageDdiziList = ({
	orderId,
	keyword,
	fixedDate,
}: {
	orderId: string;
	keyword: string | null;
	fixedDate: Date;
}) => {
	const selectedCartLines = useAtomValue(OrderState.cart.lines.excessChargeSelector);
	const jungsanInfo = StoreInfoState.useCurStoreJungsanInfo();
	const localSettings = PosElectronStoreState.usePosMainPageLocalSettings().data;
	const curDeviceInfo = StoreInfoState.useCurDeviceInfoForOrder();
	const 띠지프린터라벨출력 = PrintReceiptState.LabelPrinter.usePrintLabelPrinter({
		labelPrinterList: curDeviceInfo.labelPrinterList,
	});

	// const ddizis = ExitQueryState.useDdiziListInOrder(orderId, keyword || '', now, false);
	const FixedDdizis = ExitQueryState.useDdiziListInOrder(orderId, keyword || '', fixedDate, false);

	const availableDdizis = useMemo(
		() => FixedDdizis.filter((ddizi) => DdiziState.utils.isDdiziSelectableForJungsan(ddizi)),
		[FixedDdizis]
	);
	const cartDispatch = OrderState.cart.useDispatcher();

	const adjustTime = DdiziState.actions.useAdjustTime();
	const handleAllToggle = () => {
		cartDispatch({
			type: 'EXCESS_CHARGE',
			subAction: {
				type: 'TOGGLE_ALL',
				ddizis: availableDdizis.filter((ddizi) =>
					DdiziState.utils.isDdiziSelectableForJungsan(ddizi)
				),
				jungsanInfo,
			},
		});
	};

	const handleAdjustTime = (time: number) => () => {
		adjustTime.mutate(
			selectedCartLines.map((cl) => ({
				ddiziId: cl.ddiziUsageId,
				time: time,
			}))
		);
	};

	const { storeId, enterpriseId } = useAtomValue(StoreInfoState.curStoreAndDevice);
	const storeInfo = useAtomValue(StoreInfoState.curStoreInfo);
	const { print영수증띠지Async } = PrintReceiptState.useReceiptDdiziPrinter({
		storeId,
		deviceType: 'POS',
	});

	const handle재발권 = async () => {
		for (const cartline of selectedCartLines) {
			const ddizi = availableDdizis.find((ddizi) => ddizi.id === cartline.ddiziUsageId);
			if (ddizi) {
				const ddiziData = { ...DdiziState.utils.getDdizi인쇄Data(ddizi) };
				let 바코드총데이터: TicketMappingInfoType | undefined;
				try {
					if (enterpriseId === 'vaunce')
						바코드총데이터 = await MappingTicketApis.getTicketMappingInfo({
							ticketId: ddizi.id,
						});
				} catch {
					// 넘어가
				}

				const 권종 = ddiziData.message.labelName.includes('키즈')
					? '키즈'
					: ddiziData.message.labelName.includes('보호자')
					? '보호자'
					: '일반';

				await 띠지프린터라벨출력({
					...ddiziData,
					meta: 바코드총데이터
						? {
								고객명: 바코드총데이터.mappingMember?.name,
								생년월일: 바코드총데이터.mappingMember?.birth,
								성별: 바코드총데이터.mappingMember?.gender,
								권종,
						  }
						: undefined,
					deviceType: 'POS',
					...PrintReceiptState.LabelPrinter.getDesignOptions({
						localSettings,
						deviceNum: ddiziData.deviceNum,
					}),
					storeName: storeInfo.name,
					modelName:
						curDeviceInfo.labelPrinterList.find((p) => p.logicalName === ddiziData.deviceNum)
							?.modelName ?? '',
				});

				// 영수증 띠지 프린터를 쓴다면
				await print영수증띠지Async({
					ddizi,
				});
			}
		}
	};

	const setBeaconTagCount = useSetAtom(beaconTagCountAtom);
	const beaconTagCount = useMemo(
		() =>
			selectedCartLines.filter((cl) => {
				const ddizi = FixedDdizis.find((d) => d.id === cl.ddiziUsageId);
				return ddizi?.beaconTagId != null;
			}).length,
		[selectedCartLines, FixedDdizis]
	);
	useEffect(() => {
		setBeaconTagCount(beaconTagCount);
	}, [beaconTagCount, setBeaconTagCount]);

	const noSelected = selectedCartLines.length === 0;

	const exitedDdiziIdsToday = useMemo(
		() =>
			FixedDdizis.filter(
				(ddizi) => ddizi.status === 'EXITED' && dayjs(ddizi.publishInfo.published).isToday()
			).map((ddizi) => ddizi.id),
		[FixedDdizis]
	);
	const { data: recentHistoryByDdiziId } = useQuery({
		...DdiziQueryState.keys.recentHistoryBulk({ ddiziIds: exitedDdiziIdsToday }),
	});

	return (
		<Stack
			height={'100%'}
			sx={{ overflowY: 'auto' }}>
			<Stack
				direction={'row'}
				justifyContent={'flex-end'}
				spacing={2}
				pr={2}>
				<TicketCompleteButton
					label={'전체 선택'}
					onClick={handleAllToggle}
				/>
				<TicketPrintButton
					label={'재발권'}
					disabled={noSelected}
					onClick={handle재발권}
				/>
				<TickeTimePlusButton
					label={'10분 추가'}
					disabled={noSelected}
					onClick={handleAdjustTime(10)}
				/>
				<TickeTimeMinusButton
					label={'10분 감소'}
					disabled={noSelected}
					onClick={handleAdjustTime(-10)}
				/>
			</Stack>
			<Stack
				flex={1}
				sx={{ overflowY: 'auto' }}>
				<SimpleScrollbar>
					<List>
						{FixedDdizis.map((ddizi, idx) => {
							const selected = selectedCartLines.map((cl) => cl.ddiziUsageId).includes(ddizi.id);
							return (
								<SelectableListItem
									selected={selected}
									disabled={
										!availableDdizis.map((ddizi) => ddizi.id).includes(ddizi.id) ||
										!DdiziState.utils.isDdiziSelectableForJungsan(ddizi)
									}
									onClick={(e) => {
										cartDispatch({
											type: 'EXCESS_CHARGE',
											subAction: { type: 'TOGGLE', ddizi: ddizi, jungsanInfo },
										});
									}}
									key={idx}>
									<ExitPageDdiziDisplay
										width={'100%'}
										ddizi={ddizi}
										itemButton={
											dayjs(ddizi.publishInfo.published).isToday() && (
												<Stack
													direction={'row'}
													alignItems={'center'}
													spacing={1}>
													<Stack
														direction={'row'}
														spacing={1}
														sx={{ pointerEvents: 'all' }}>
														<WithDialog
															dialog={(open, closeDialog) => (
																<DdiziDetailDialog
																	open={open}
																	closeDialog={closeDialog}
																	ddizi={ddizi}
																/>
															)}>
															{(openDialog) => <DetailButton onClick={openDialog} />}
														</WithDialog>
														<DdiziEnterExitButtons
															ddizi={ddizi}
															recentHistory={recentHistoryByDdiziId?.[ddizi.id]}
														/>
													</Stack>
													<Checkbox checked={selected} />
												</Stack>
											)
										}
									/>
								</SelectableListItem>
							);
						})}
					</List>
				</SimpleScrollbar>
			</Stack>
		</Stack>
	);
};
